<mat-toolbar color="primary" class="action-toolbar">
    <button mat-icon-button (click)="returnToParent()" matTooltip="Back">
        <mat-icon color="primary">arrow_back_ios</mat-icon>
    </button>
    <h3>{{ patient | patientHeading:'Clinical Note' }}</h3>
    <div>
        <button mat-icon-button (click)="openAddUsingImageDialog()"
            matTooltip="Scan a picture to obtain note details for adding"><mat-icon
                class="ai-feature">document_scanner</mat-icon></button>

        <button mat-icon-button *ngIf="editingNote && editingNote.id" (click)="deleteNote()" matTooltip="Delete Note">
            <mat-icon color="warn">delete</mat-icon>
        </button>
        <button mat-icon-button [disabled]="form.invalid" (click)="saveNote()" matTooltip="Save Note">
            <mat-icon [color]="form.invalid ? '' : 'primary'">save</mat-icon>
        </button>
    </div>
</mat-toolbar>

<div class="note-details-container">
    <form [formGroup]="form">
        <mat-card>
            <mat-form-field appearance="outline">
                <mat-label>Date</mat-label>
                <input matInput [matDatepicker]="dateDatePicker" formControlName="date" placeholder="DD/MM/YYYY"
                    [max]="todayDate">
                <mat-datepicker-toggle matSuffix [for]="dateDatePicker"></mat-datepicker-toggle>
                <mat-datepicker #dateDatePicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Note Type</mat-label>
                <mat-select formControlName="noteType" (selectionChange)="selectNoteType($event)">
                    <mat-option *ngFor="let noteType of noteTypes" [value]="noteType.id">
                        {{ noteType.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <div class="toggle-container">
                <mat-slide-toggle formControlName="isProcedure" color="primary" 
                    [disabled]="isBillingNote"
                    (change)="onIsProcedureToggle($event)">Procedure
                    Note</mat-slide-toggle>
                <mat-slide-toggle formControlName="isBilling" color="primary" 
                    [disabled]="isProcedureNote"
                    (change)="onIsBillingToggle($event)" class="m-l-5">Billing
                    Note</mat-slide-toggle>
            </div>

            <mat-form-field appearance="outline">
                <mat-label>{{isProcedureNote ? 'Procedure' : 'Title'}}</mat-label>
                <input matInput formControlName="title">
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Notes</mat-label>
                <textarea matInput appFindReplace cdkTextareaAutosize="true" rows="10" formControlName="notes"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline" *ngIf="!stateUnit">
                <mat-label>Coding Bundle</mat-label>
                <mat-select formControlName="codingBundle" (selectionChange)="selectCodingBundle($event)">
                    <mat-option *ngFor="let bundle of codingBundles" [value]="bundle">
                        {{ bundle.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <div class="toggle-container" *ngIf="!stateUnit">
                <mat-slide-toggle formControlName="paidCash" color="primary" (change)="togglePaidCash($event)">Paid Up
                    Front</mat-slide-toggle>
                <mat-slide-toggle formControlName="noCharge" color="primary" (change)="toggleNoCharge($event)"
                    class="m-l-5">Pro Bono</mat-slide-toggle>
            </div>

            <app-procedure-code-selector [initialCodes]="selectedProcedureCodes"
                (codesChanged)="onProcedureCodesChanged($event)" #procedureCodeSelector
                [patient]="patient"></app-procedure-code-selector>

            <div class="icd-container m-b-5" *ngIf="suggestedProcedureCodes?.length && !stateUnit">
                <h4>Suggested Procedure Codes</h4>
                <mat-list class="full-width">
                    <mat-list-item class="auto-height" *ngFor="let code of suggestedProcedureCodes">
                        <div class="icd-item">
                            <div class="icd-details">
                                <p matLine class="icd-title">{{code.code}} - {{code.description}}</p>
                            </div>
                            <div class="view-icon">
                                <button mat-icon-button color="primary" matTooltip="Add this code to the note"
                                    (click)="addProcedureCode($event, code)">
                                    <mat-icon>library_add</mat-icon>
                                </button>
                            </div>
                        </div>
                    </mat-list-item>
                </mat-list>
            </div>

            <app-file-upload [fileObjects]="loadedNoteMediaObjects" [allowMultiple]="true"
                [allowedFileTypes]="'image/*, application/pdf'" title="Note Media"
                (fileUpload)="onNoteMediaUpload($event)" (fileDelete)="onNoteMediaDelete($event)"
                (filesEdited)="onFilesEdited($event)" [cameraEnabled]="true" [enableEditing]="true">
            </app-file-upload>
        </mat-card>

        <!-- Moved OUT of the note card at the user's request: these are not note fields.
             A risk item is governance correspondence and a suggested task is an action -
             neither belongs among the clinical record's own inputs. They now sit under the
             card, in the same scroll flow. -->
        <!--
          F2 — the extractor's suggested FACILITY ISSUES, from the ward-round hand-off.

          The SAME component the AI request detail renders (E2), not a copy: the verbatim
          quote (D8) and the dedupe guard (E4) are controls on a document that ends up on a
          doctor's letterhead (R4), and a control that exists twice can be relaxed in one
          copy and not the other.

          SINCE M5 THIS IS THE FALLBACK PLACEMENT ONLY. The suggestions now live inside the
          RISK MANAGEMENT panel below, which is gated on the note having an `aiRequestId`.
          This block survives for the one case that panel cannot serve: suggestions handed
          over in memory with no request behind them. The two gates are mutually exclusive
          (`!hasAiRequest`), so the list never renders twice.

          It sits OUTSIDE the `<form>`'s field flow but inside the card, because none of it is
          a note field: a risk item is governance correspondence, not part of the clinical
          record (§1).
        -->
        <div class="icd-container m-b-5" *ngIf="!hasAiRequest && hasRiskSuggestions">
            <app-risk-suggestion-list [suggestions]="riskSuggestions" [addedIssues]="addedRiskIssues"
                [disabled]="addingRiskItem" heading="Suggested Facility Issues"
                (addSuggestion)="onAddRiskSuggestion($event)">
            </app-risk-suggestion-list>
        </div>

        <!--
          M3/M5 — the SAME pattern for admin tasks, and the same markup the discharge summary
          uses for its SUGGESTED TASKS list (`discharge-details.component.html:404-432`), so a
          suggested task looks and behaves identically wherever a doctor meets one.
        -->
        <mat-expansion-panel [expanded]="false" class="m-b-10" *ngIf="hasAiRequest">
            <mat-expansion-panel-header>
                <mat-panel-title>SUGGESTED TASKS</mat-panel-title>
            </mat-expansion-panel-header>

            <!-- STATE 1 — still running. -->
            <div class="detection-loading" *ngIf="detectionPending">
                <mat-spinner diameter="24"></mat-spinner>
                <span>Reviewing the recording for admin tasks…</span>
            </div>

            <!-- STATE 2 — settled, with results. `detectionSettled` for the same reason as above. -->
            <div class="icd-container" *ngIf="detectionSettled && hasSuggestedTasks">
                <mat-list class="full-width">
                    <mat-list-item class="auto-height" *ngFor="let task of suggestedTaskList">
                        <div class="icd-item">
                            <div class="icd-details">
                                <p matLine class="icd-title">{{task.task}}</p>
                                <p matLine class="icd-score">Relevance: {{task.score}}%</p>
                            </div>
                            <div class="view-icon">
                                <button mat-icon-button type="button" [matTooltip]="task.reason">
                                    <mat-icon>info</mat-icon>
                                </button>
                                <button mat-icon-button type="button" color="primary"
                                    *ngIf="canAddTask(task.task)"
                                    matTooltip="Add this task to the patient file"
                                    (click)="addTask($event, task)">
                                    <mat-icon>library_add</mat-icon>
                                </button>
                            </div>
                        </div>
                    </mat-list-item>
                </mat-list>
            </div>

            <!-- STATE 3 — finished, nothing found. -->
            <p class="grey-text detection-empty" *ngIf="detectionComplete && !hasSuggestedTasks">
                No admin tasks were identified in this recording.
            </p>

            <!-- STATE 4 — the pass itself failed. -->
            <p class="grey-text detection-failed" *ngIf="detectionFailed">
                Admin task detection could not be completed for this recording. You can still add
                a task to the patient file by hand.
            </p>

            <!-- STATE 5 — no background pass ran, and nothing in the column. See above. -->
            <p class="grey-text detection-not-run" *ngIf="detectionNotRun && !hasSuggestedTasks">
                Admin task detection was not run for this note.
            </p>
        </mat-expansion-panel>
        <!--
          ══ M5 — BACKGROUND DETECTION, AS TWO COLLAPSIBLE SECTIONS ═════════════════════

          *"inside the note-detail component we show this as its own collapsible section with
          a loader while that background process is running (similar to the additional tabs in
          the discharge component)."*

          BOTH panels are gated on `hasAiRequest` and nothing else. A hand-written note has no
          AIRequest, so no detection pass ever ran for it and neither panel appears — not
          empty, not loading, absent.

          THREE STATES, THREE DIFFERENT RENDERINGS, and the difference is the point (D14):

            pending   → a spinner and "Reviewing…". No list, no message.
            complete  → the suggestion list if there is one, otherwise a plain grey
                        "nothing found" line. This is the ONLY state allowed to make that
                        claim, because it is the only one in which a NULL column means the
                        detector looked and came back empty.
            failed    → says so. Never dressed up as "nothing found": a doctor who is told no
                        facility issues were heard, when in fact nothing listened, has been
                        given a false negative about a governance record.

          `'none'` (a hand-written note's default, and every request predating the column)
          renders none of the three — it makes no claim at all.
        -->
        <mat-expansion-panel [expanded]="false" class="m-t-10 m-b-10" *ngIf="hasAiRequest">
            <mat-expansion-panel-header>
                <mat-panel-title>RISK MANAGEMENT</mat-panel-title>
            </mat-expansion-panel-header>

            <!-- STATE 1 — still running. -->
            <div class="detection-loading" *ngIf="detectionPending">
                <mat-spinner diameter="24"></mat-spinner>
                <span>Reviewing the recording for facility issues…</span>
            </div>

            <!--
              STATE 2 — settled, with results. `detectionSettled`, not `detectionComplete`: a
              CONSULT note also carries an aiRequestId, and `GenerateAIOutput` runs its
              detectors inline, so those requests hold real suggestions under a status of
              'none'. Gating on 'complete' alone would render an empty panel over them.
            -->
            <div class="icd-container" *ngIf="detectionSettled && hasRiskSuggestions">
                <app-risk-suggestion-list [suggestions]="riskSuggestions" [addedIssues]="addedRiskIssues"
                    [disabled]="addingRiskItem" heading="Suggested Facility Issues"
                    (addSuggestion)="onAddRiskSuggestion($event)">
                </app-risk-suggestion-list>
            </div>

            <!-- STATE 3 — finished, nothing found. A different thing entirely from STATE 1. -->
            <p class="grey-text detection-empty" *ngIf="detectionComplete && !hasRiskSuggestions">
                No facility issues were identified in this recording.
            </p>

            <!-- STATE 4 — the pass itself failed. Not a result, and not shown as one. -->
            <p class="grey-text detection-failed" *ngIf="detectionFailed">
                Facility issue detection could not be completed for this recording. Nothing was
                identified because nothing ran — you can still add an issue to the register by hand.
            </p>

            <!--
              STATE 5 — no background pass ran, and there is nothing in the column either.
              Deliberately NOT the STATE 3 wording: "none were found" would be a claim about a
              search that never happened.
            -->
            <p class="grey-text detection-not-run" *ngIf="detectionNotRun && !hasRiskSuggestions">
                Facility issue detection was not run for this note.
            </p>
        </mat-expansion-panel>

    </form>

    <div class="mobile-spacer"></div>
</div>

<!--
  M8 — the Task Details dialog, identical to `discharge-details.component.html:453` and
  `patient-ai-request-detail.component.html:2313`. Same fields, same validation, same shape of
  result, so a task added from a ward round is indistinguishable from one added anywhere else.

  The datepicker template refs are named `task*` rather than reusing `#dateDatePicker`, which is
  already taken by the note's own Date field at the top of this template.
-->
<!-- In the chat window's frame (src/_cura-window.scss, panel class ai-recording-panel), as the
     admission sheet scanner is. -->
<ng-template #scanNoteDetailsDialog>
    <div class="cw-header">
        <mat-icon class="kind">document_scanner</mat-icon>
        <div class="title">
            <span class="name">Upload note image</span>
            <span class="subtitle">Photograph or upload a handwritten note and MedicAI reads it into this note</span>
        </div>
    </div>
    <div mat-dialog-content class="cw-body">

        <div class="p-t-10">
            <div class="ai-locked" *ngIf="lockProcessing && !isPremium">
                <mat-icon>error</mat-icon>
                <p class="p-l-10">You do not have enough AI tokens to continue utilizing AI features. Please recharge
                    your token balance by navigating to settings and selecting the "Subscriptions & Usage" tab.</p>
            </div>

            <ng-container *ngIf="!lockProcessing">

                <div class="token-balance-warning" *ngIf="!isPremium && tokenBalance && tokenBalance <= 100">
                    <mat-icon>warning</mat-icon>
                    <p class="p-l-10">Your token balance is currently R{{ tokenBalance }}. Please
                        recharge soon to ensure you are able to continue utilizing AI features.</p>
                </div>

                <app-file-upload [fileObjects]="[]" [allowMultiple]="false" [allowedFileTypes]="'image/*'"
                    title="Note Image" (fileUpload)="onNoteScanUpload($event)" (fileDelete)="onNoteScanDelete($event)"
                    *ngIf="!noteScanProcessing" [cameraEnabled]="true" [previewEnabled]="true">
                </app-file-upload>


                <ng-container *ngIf="noteScanProcessing">
                    <div class="cw-processing">
                        <mat-spinner diameter="24"></mat-spinner>
                        <span>Processing...</span>
                    </div>
                    <div class="cw-status"
                        [ngClass]="!noteScanProcessing ? 'completed' : 'pending'">
                        <span>Extracting note information</span>
                    </div>

                    <p class="cw-hint"><strong>Note: </strong>MedicAI can make mistakes. We advise that you check the
                        content
                        of the output carefully before accepting it into the patient file.</p>
                </ng-container>

            </ng-container>

        </div>

        <p class="cw-hint">
            By proceeding, you agree to the <a href="assets/documents/IQject Terms of service - v0.5.pdf"
                target="_blank">terms of service</a>
            and <a href="assets/documents/IQject privacy notice - 21 January 2025.pdf" target="_blank">privacy
                policy</a>.
        </p>

    </div>

    <div mat-dialog-actions class="cw-actions dialog-actions">
        <span class="spacer"></span>
        <button mat-stroked-button class="quiet-button" [mat-dialog-close]>Close</button>
    </div>

</ng-template>